import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useSyncExternalStore,
} from "react";

/**
 * SearchRegistry — a context where sidebar portals register their searchable
 * sub-items so the search modal can drill into them.
 *
 * Usage in a portal:
 *
 *   const { register, unregister } = useSearchRegistry();
 *
 *   useEffect(() => {
 *     register("/chat", [
 *       { id: "thread-1", label: "Project Alpha", path: "/chat/thread-1", icon: Hash },
 *       { id: "dm-2",     label: "Jane Smith",    path: "/chat/dm-2",     icon: User },
 *     ]);
 *     return () => unregister("/chat");
 *   }, [threads]);
 *
 * Item shape:
 *   {
 *     id:        string           — unique within the section
 *     label:     string           — display name (also searched against)
 *     keywords?: string[]         — extra search terms
 *     path?:     string           — if set, navigate() is called on select
 *     event?:    { name, detail } — if set, a custom event is dispatched on select
 *     icon?:     LucideComponent  — optional icon
 *     iconColor?: string          — optional icon color
 *     children?: Item[]           — nested sub-items for further drill-down
 *   }
 *
 * Navigation priority: if `path` is set it takes precedence. Otherwise `event`
 * is dispatched. This lets portals like Wiki fire sidebar events instead of
 * doing a raw navigate.
 */

const RegistryContext = createContext(null);

export function SearchRegistryProvider({ children }) {
  // Store: { [sectionPath]: Item[] }
  const storeRef = useRef({});
  const versionRef = useRef(0);
  const listenersRef = useRef(new Set());

  const notify = useCallback(() => {
    versionRef.current += 1;
    listenersRef.current.forEach((fn) => fn());
  }, []);

  const register = useCallback(
    (sectionPath, items) => {
      storeRef.current[sectionPath] = items;
      notify();
    },
    [notify],
  );

  const unregister = useCallback(
    (sectionPath) => {
      delete storeRef.current[sectionPath];
      notify();
    },
    [notify],
  );

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const getSnapshot = useCallback(() => versionRef.current, []);

  const value = { storeRef, register, unregister, subscribe, getSnapshot };

  return (
    <RegistryContext.Provider value={value}>
      {children}
    </RegistryContext.Provider>
  );
}

/**
 * Hook for portals — returns { register, unregister }.
 */
export function useSearchRegistry() {
  const ctx = useContext(RegistryContext);
  if (!ctx)
    throw new Error(
      "useSearchRegistry must be used within <SearchRegistryProvider>",
    );
  return { register: ctx.register, unregister: ctx.unregister };
}

/**
 * Hook for the search modal — returns the current registry snapshot.
 * Re-renders whenever any portal calls register/unregister.
 */
export function useSearchRegistryData() {
  const ctx = useContext(RegistryContext);
  if (!ctx)
    throw new Error(
      "useSearchRegistryData must be used within <SearchRegistryProvider>",
    );

  // Subscribe to changes so we re-render
  const version = useSyncExternalStore(ctx.subscribe, ctx.getSnapshot);

  // Return both the store and version — version is used as a useMemo
  // dependency to ensure memos recompute when registry contents change
  return { store: ctx.storeRef.current, version };
}
